import { useState, useEffect } from "react";
// @mui
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from '@mui/lab';
// hooks
import useCompany from '../../hooks/useCompany';
// components
import Iconify from "../../components/Iconify";
import SvgIconStyle from "../../components/SvgIconStyle";
import DIOText from "../../components/DIOText";


export default function CoreTimeDialog({ isOpen, closeDialog, initialCoreTime, crewId }) {
  const { crews } = useCompany();
  const crew = crews.find(crew => crew.id === parseInt(crewId)) || {};
  const [monTime, setMonTime] = useState(['', '']);
  const [tueTime, setTueTime] = useState(['', '']);
  const [wedTime, setWedTime] = useState(['', '']);
  const [thuTime, setThuTime] = useState(['', '']);
  const [friTime, setFriTime] = useState(['', '']);
  const [satTime, setSatTime] = useState(['', '']);
  const [sunTime, setSunTime] = useState(['', '']);

  const closeWithReset = () => {
    closeDialog();
  }

  const setTimeJson = (setFunc, timeStart, timeEnd) => {
    if (typeof timeStart === 'object' && typeof timeEnd === 'object') {
      setFunc([JSON.stringify(timeStart, Object.keys(timeStart).sort()), JSON.stringify(timeEnd, Object.keys(timeStart).sort())]);
    } else {
      setFunc([timeStart, timeEnd]);
    }
  }

  useEffect(() => {
    if (initialCoreTime && Array.isArray(initialCoreTime)) {
      initialCoreTime.forEach((time) => {
        switch (time.weekday) {
          case 0:
            setTimeJson(setMonTime, time.start, time.end);
            break;
          case 1:
            setTimeJson(setTueTime, time.start, time.end);
            break;
          case 2:
            setTimeJson(setWedTime, time.start, time.end);
            break;
          case 3:
            setTimeJson(setThuTime, time.start, time.end);
            break;
          case 4:
            setTimeJson(setFriTime, time.start, time.end);
            break;
          case 5:
            setTimeJson(setSatTime, time.start, time.end);
            break;
          case 6:
            setTimeJson(setSunTime, time.start, time.end);
            break;
          default:
            break;
        }
      })
    } else {
      // reset core time when initial core time value is changed
      setTimeJson(setMonTime, '', '');
      setTimeJson(setTueTime, '', '');
      setTimeJson(setWedTime, '', '');
      setTimeJson(setThuTime, '', '');
      setTimeJson(setFriTime, '', '');
      setTimeJson(setSatTime, '', '');
      setTimeJson(setSunTime, '', '');
    }
  }, [initialCoreTime]);
  
  return (
    <Dialog open={isOpen} onClose={closeWithReset} fullWidth maxWidth="dg">
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h5">
          출퇴근 정책
        </Typography>
        <Stack direction="row" spacing={.5} alignItems="flex-start" sx={{ my: 3 }}>
          <SvgIconStyle src={"/icons/icon-check-mono.svg"} sx={{ minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, color: "text.disabled" }} />
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              크루는 업무시 출퇴근 시간을 기록합니다.
            </Typography>
            <Typography variant="body2">
              출퇴근 기록시 카카오톡으로 알려드려요.
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={.5} alignItems="flex-start">
          <SvgIconStyle src={"/icons/icon-check-mono.svg"} sx={{ minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, color: "text.disabled" }} />
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              출퇴근할 때마다, 크루가 오늘 한 일을 보고받을 수 있어요.
            </Typography>
            <Typography variant="body2">
              크루는 출근 시 진행할 업무를 기록해야 출근 기록이 시작되며, 퇴근 시 당일 진행한 업무를 기록해야 퇴근 시간이 기록됩니다.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              출퇴근 시 업무 공유가 필요하지 않다면, 크루와 협의 후 <DIOText /> 카카오톡 채널로 알려주세요.
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={.5} alignItems="flex-start" sx={{ mb: 1 }}>
          <Iconify icon={'eva:clock-outline'} sx={{ minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, color: "text.disabled" }} />
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {crew?.nickname} 크루의 업무 코어 타임
            </Typography>
            <Stack spacing={.5}>
              <RenderWeekday weekdayName="월" weekdayVariable={monTime} />
              <RenderWeekday weekdayName="화" weekdayVariable={tueTime} />
              <RenderWeekday weekdayName="수" weekdayVariable={wedTime} />
              <RenderWeekday weekdayName="목" weekdayVariable={thuTime} />
              <RenderWeekday weekdayName="금" weekdayVariable={friTime} />
              <RenderWeekday weekdayName="토" weekdayVariable={satTime} />
              <RenderWeekday weekdayName="일" weekdayVariable={sunTime} />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={{ xs: 1, sm: 4 }} sx={{ width: 1, justifyContent: "flex-end" }}>
          <LoadingButton
            variant="contained"
            size="large"
            sx={{ px: 4 }}
            onClick={closeDialog}
          >
            확인했어요
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

function RenderWeekday({ weekdayName, weekdayVariable }) {
  const from = weekdayVariable && weekdayVariable[0] ? JSON.parse(weekdayVariable[0]) : null;
  const to = weekdayVariable && weekdayVariable[1] ? JSON.parse(weekdayVariable[1]) : null;

  if (!from && !to) return <></>;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant={"body2"}>
        {weekdayName} :
      </Typography>
      <Typography>
        {from?.label} ~ {to?.label}
      </Typography>
    </Stack> 
  )
}
